/**
 * Authoritative can’t-miss registry (15 chief complaints) — DDX-Plus Tier 0 safety net.
 * Sources: emergency medicine references (Tintinalli, Rosen); IDs align with docs/DDX-PLUS-DECISION-SUPPORT-PLAN.md.
 */

const mf = (h = 1, e = 1, d = 0) => ({ history: h, exam: e, diagnostic: d });

function dx(id, name, rationale, minimumFeatureCount = mf(), requiredAtypicalPresentations = []) {
  return { id, name, rationale, minimumFeatureCount, requiredAtypicalPresentations };
}

function mimic(id, name, mimics) {
  return { id, name, mimics };
}

function disc(feature, criticalFor, type) {
  return { feature, criticalFor, type };
}

/** @type {Record<string, Object>} */
const REGISTRY = {
  cc_chest_pain: {
    id: 'cc_chest_pain',
    chiefComplaint: 'Chest pain',
    requiredDiagnoses: [
      dx('dx_ami', 'Acute myocardial infarction / ACS', 'Leading preventable cause of death in CP; ECG/labs time-sensitive.', mf(2, 1, 1)),
      dx('dx_pe', 'Pulmonary embolism', 'May present as CP or SOB only; PE can be fatal if missed.', mf(2, 1, 1)),
      dx('dx_ad', 'Aortic dissection', 'Catastrophic if missed; consider in tearing CP, radiation, pulse deficit.', mf(2, 1, 1)),
      dx('dx_ptx', 'Tension pneumothorax', 'Immediate decompression when suspected.', mf(1, 2, 0)),
      dx('dx_tam', 'Cardiac tamponade', 'Beck triad often incomplete; consider post-procedure / malignancy.', mf(1, 2, 1)),
      dx('dx_boerhaave', 'Esophageal rupture (Boerhaave)', 'Post-emesis CP; mediastinal air / sepsis.', mf(2, 1, 1)),
    ],
    requiredMimics: [
      mimic('m_gc', 'GERD / musculoskeletal chest pain', ['Acute coronary syndrome', 'Pulmonary embolism']),
      mimic('m_cost', 'Costochondritis', ['Acute coronary syndrome']),
    ],
    requiredDiscriminators: [
      disc('ECG ischemia or STEMI pattern', ['dx_ami'], 'diagnostic'),
      disc('D-dimer / CTPA pathway when PE suspected', ['dx_pe'], 'diagnostic'),
      disc('Widened mediastinum or pulse deficit', ['dx_ad'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_dyspnea: {
    id: 'cc_dyspnea',
    chiefComplaint: 'Dyspnea / shortness of breath',
    requiredDiagnoses: [
      dx('dx_pe', 'Pulmonary embolism', 'Common mimic of many presentations; risk stratify.', mf(2, 1, 1)),
      dx('dx_ptx', 'Tension pneumothorax', 'Obstructive shock; immediate intervention.', mf(1, 2, 0)),
      dx('dx_tam', 'Cardiac tamponade', 'Hypotension + distended neck veins + clear lungs.', mf(1, 2, 1)),
      dx('dx_anaphylaxis', 'Anaphylaxis', 'Airway and hemodynamic emergency.', mf(2, 2, 0)),
      dx('dx_chf', 'Acute heart failure exacerbation', 'Flash pulmonary edema; consider ACS overlap.', mf(2, 2, 1)),
      dx('dx_fba', 'Foreign body airway obstruction', 'Especially pediatric / altered; sudden onset.', mf(2, 2, 0)),
    ],
    requiredMimics: [mimic('m_ast', 'Asthma exacerbation', ['Pulmonary embolism', 'Anaphylaxis'])],
    requiredDiscriminators: [
      disc('Sudden pleuritic pain + hypoxia', ['dx_pe'], 'history'),
      disc('Unilateral breath sounds', ['dx_ptx'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_abdominal_pain: {
    id: 'cc_abdominal_pain',
    chiefComplaint: 'Abdominal pain',
    requiredDiagnoses: [
      dx('dx_aaa_rupture', 'AAA rupture / symptomatic AAA', 'Elderly / vasculopath; pulsatile mass; syncope + pain.', mf(2, 1, 1)),
      dx('dx_mesenteric', 'Mesenteric ischemia', 'Pain out of proportion; AF, post-prandial pain.', mf(2, 1, 1)),
      dx('dx_ectopic', 'Ectopic pregnancy', 'Any reproductive-age person with pain or bleeding.', mf(2, 1, 1)),
      dx('dx_appendicitis', 'Appendicitis', 'Common surgical emergency; atypical in extremes of age.', mf(2, 2, 1)),
      dx('dx_bowel_obstruction', 'Bowel obstruction / perforation', 'Distension, peritonitis, free air.', mf(2, 2, 1)),
      dx('dx_splenic_rupture', 'Splenic rupture', 'Trauma / mono; hemoperitoneum.', mf(2, 1, 1)),
    ],
    requiredMimics: [mimic('m_gastro', 'Gastroenteritis', ['Mesenteric ischemia', 'Ectopic pregnancy'])],
    requiredDiscriminators: [
      disc('Beta-hCG in reproductive-age patients', ['dx_ectopic'], 'diagnostic'),
      disc('Peritoneal signs / rigidity', ['dx_bowel_obstruction', 'dx_appendicitis'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_headache: {
    id: 'cc_headache',
    chiefComplaint: 'Headache',
    requiredDiagnoses: [
      dx('dx_sah', 'Subarachnoid hemorrhage', 'Thunderclap headache; sentinel bleed risk.', mf(2, 1, 1)),
      dx('dx_cns_infection', 'Meningitis / encephalitis', 'Fever, neck stiffness, altered mental status.', mf(2, 2, 1)),
      dx('dx_stroke', 'CVA / stroke', 'Sudden focal deficit; time-sensitive therapy.', mf(2, 2, 1)),
      dx('dx_cvt', 'Cerebral venous thrombosis', 'Headache + risk factors; can mimic migraine.', mf(2, 1, 1)),
      dx('dx_gca', 'Temporal (giant cell) arteritis', 'Age >50; jaw claudication; vision loss risk.', mf(2, 1, 1)),
      dx('dx_cns_mass', 'CNS mass with herniation', 'Focal neuro, papilledema, progressive course.', mf(2, 2, 1)),
    ],
    requiredMimics: [mimic('m_migraine', 'Migraine', ['Subarachnoid hemorrhage', 'Meningitis'])],
    requiredDiscriminators: [
      disc('Sudden maximal intensity at onset', ['dx_sah'], 'history'),
      disc('Focal neuro deficit', ['dx_stroke'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_syncope: {
    id: 'cc_syncope',
    chiefComplaint: 'Syncope',
    requiredDiagnoses: [
      dx('dx_arrhythmia', 'Cardiac arrhythmia (VT/VF, complete heart block)', 'Sudden collapse without prodrome suggests cardiac.', mf(2, 2, 1)),
      dx('dx_ami', 'Acute myocardial infarction', 'ACS may present as syncope.', mf(2, 1, 1)),
      dx('dx_pe', 'Pulmonary embolism', 'Hypoxia, tachycardia, risk factors.', mf(2, 1, 1)),
      dx('dx_ad', 'Aortic dissection', 'Chest/back pain, pulse deficit.', mf(2, 1, 1)),
      dx('dx_aaa_rupture', 'AAA rupture', 'Abdominal pain, hypotension, pulsatile mass.', mf(2, 1, 1)),
      dx('dx_ectopic', 'Ectopic pregnancy', 'Hypovolemic shock in pregnancy.', mf(2, 1, 1)),
    ],
    requiredMimics: [mimic('m_vvs', 'Vasovagal syncope', ['Cardiac arrhythmia'])],
    requiredDiscriminators: [
      disc('ECG for ischemia / arrhythmia', ['dx_arrhythmia', 'dx_ami'], 'diagnostic'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_altered_mental_status: {
    id: 'cc_altered_mental_status',
    chiefComplaint: 'Altered mental status',
    requiredDiagnoses: [
      dx('dx_hypoglycemia', 'Hypoglycemia', 'Rapidly reversible; check glucose immediately.', mf(1, 1, 1)),
      dx('dx_stroke', 'CVA / stroke', 'Focal findings; last known well.', mf(2, 2, 1)),
      dx('dx_cns_infection', 'Meningitis / encephalitis', 'Fever, meningismus, immunocompromise.', mf(2, 2, 1)),
      dx('dx_ich', 'Intracranial hemorrhage', 'Head trauma, anticoagulation, sudden deficit.', mf(2, 2, 1)),
      dx('dx_toxin', 'Drug / toxin ingestion', 'Oversedation, street drugs, alcohol.', mf(2, 1, 1)),
      dx('dx_sepsis', 'Sepsis', 'Infection source; hypotension / organ dysfunction.', mf(2, 2, 1)),
    ],
    requiredMimics: [mimic('m_postictal', 'Postictal state', ['Intracranial hemorrhage', 'Meningitis'])],
    requiredDiscriminators: [
      disc('Point-of-care glucose', ['dx_hypoglycemia'], 'diagnostic'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_back_pain: {
    id: 'cc_back_pain',
    chiefComplaint: 'Back pain',
    requiredDiagnoses: [
      dx('dx_aaa_rupture', 'AAA rupture', 'Tearing to back, hypotension.', mf(2, 1, 1)),
      dx('dx_ad', 'Aortic dissection', 'Radiating pain; pulse/BP differential.', mf(2, 1, 1)),
      dx('dx_cauda', 'Cauda equina syndrome', 'Saddle anesthesia, retention, bilateral leg weakness.', mf(2, 2, 1)),
      dx('dx_epidural_abscess', 'Epidural abscess', 'Fever, IVDU, immunosuppression.', mf(2, 2, 1)),
      dx('dx_cord_compression', 'Spinal cord compression', 'Myelopathy, hyperreflexia.', mf(2, 2, 1)),
      dx('dx_retroperitoneal', 'Retroperitoneal hemorrhage', 'Anticoagulation, flank pain, drop in Hgb.', mf(2, 1, 1)),
    ],
    requiredMimics: [mimic('m_muscle', 'Muscular strain', ['Cauda equina syndrome', 'Epidural abscess'])],
    requiredDiscriminators: [
      disc('Post-void residual / urinary retention', ['dx_cauda'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_fever: {
    id: 'cc_fever',
    chiefComplaint: 'Fever',
    requiredDiagnoses: [
      dx('dx_sepsis', 'Sepsis', 'SIRS + suspected infection; early bundles.', mf(2, 2, 1)),
      dx('dx_cns_infection', 'Meningitis', 'Headache, neck stiffness, petechiae.', mf(2, 2, 1)),
      dx('dx_nec_fasciitis', 'Necrotizing fasciitis', 'Pain out of proportion; rapid spread.', mf(2, 2, 1)),
      dx('dx_endocarditis', 'Endocarditis', 'New murmur, IVDA, embolic phenomena.', mf(2, 2, 1)),
      dx('dx_febrile_neutropenia', 'Febrile neutropenia', 'Oncology / chemo; treat empirically.', mf(2, 1, 1)),
      dx('dx_epiglottitis', 'Epiglottitis (pediatric consideration)', 'Drooling, tripod, airway threat.', mf(2, 2, 0)),
    ],
    requiredMimics: [mimic('m_viral', 'Viral syndrome', ['Bacterial meningitis', 'Sepsis'])],
    requiredDiscriminators: [
      disc('Toxic appearance / hypotension', ['dx_sepsis'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_weakness: {
    id: 'cc_weakness',
    chiefComplaint: 'Weakness',
    requiredDiagnoses: [
      dx('dx_stroke', 'CVA / stroke', 'Acute focal weakness; LKW.', mf(2, 2, 1)),
      dx('dx_cord_compression', 'Spinal cord compression', 'Bilateral symptoms, sensory level.', mf(2, 2, 1)),
      dx('dx_gbs', 'Guillain-Barré syndrome', 'Ascending weakness, areflexia.', mf(2, 2, 1)),
      dx('dx_mg_crisis', 'Myasthenic crisis', 'Bulbar weakness, respiratory fatigue.', mf(2, 2, 1)),
      dx('dx_hyperkalemia', 'Hyperkalemia', 'Weakness + ECG changes.', mf(1, 1, 1)),
      dx('dx_ad', 'Aortic dissection', 'Neuro deficit from malperfusion.', mf(2, 1, 1)),
    ],
    requiredMimics: [mimic('m_fatigue', 'Deconditioning / fatigue', ['Guillain-Barré syndrome'])],
    requiredDiscriminators: [
      disc('Acute focal neuro exam', ['dx_stroke'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_seizure: {
    id: 'cc_seizure',
    chiefComplaint: 'Seizure',
    requiredDiagnoses: [
      dx('dx_status', 'Status epilepticus', 'Prolonged or recurrent without recovery.', mf(2, 2, 1)),
      dx('dx_ich', 'Intracranial hemorrhage', 'Postictal prolonged or focal deficit.', mf(2, 2, 1)),
      dx('dx_cns_infection', 'Meningitis / encephalitis', 'Fever, AMS.', mf(2, 2, 1)),
      dx('dx_eclampsia', 'Eclampsia', 'Pregnancy / postpartum hypertension.', mf(2, 2, 1)),
      dx('dx_hyponatremia', 'Hyponatremia', 'Chronic alcohol, SIADH, psychogenic polydipsia.', mf(2, 1, 1)),
      dx('dx_cns_mass', 'CNS mass', 'New-onset seizure in adult.', mf(2, 2, 1)),
    ],
    requiredMimics: [mimic('m_syncope', 'Syncope with convulsive activity', ['Status epilepticus'])],
    requiredDiscriminators: [
      disc('Glucose and sodium', ['dx_hyponatremia'], 'diagnostic'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_trauma_blunt: {
    id: 'cc_trauma_blunt',
    chiefComplaint: 'Trauma — blunt',
    requiredDiagnoses: [
      dx('dx_hemorrhagic_shock', 'Hemorrhagic shock', 'Hypotension, tachycardia, source control.', mf(2, 2, 1)),
      dx('dx_ptx', 'Tension pneumothorax', 'Unilateral breath sounds, distended neck veins.', mf(1, 2, 0)),
      dx('dx_tam', 'Cardiac tamponade', 'Beck triad; FAST.', mf(1, 2, 1)),
      dx('dx_flail', 'Flail chest', 'Respiratory failure risk.', mf(1, 2, 1)),
      dx('dx_cspine', 'C-spine injury', 'Midline tenderness, neuro deficit.', mf(2, 2, 1)),
      dx('dx_solid_organ', 'Solid organ injury', 'Splenic / hepatic laceration.', mf(2, 2, 1)),
    ],
    requiredMimics: [mimic('m_contusion', 'Soft tissue contusion', ['Hemorrhagic shock'])],
    requiredDiscriminators: [
      disc('FAST / eFAST', ['dx_tam', 'dx_solid_organ'], 'diagnostic'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_vaginal_bleeding: {
    id: 'cc_vaginal_bleeding',
    chiefComplaint: 'Vaginal bleeding',
    requiredDiagnoses: [
      dx('dx_ectopic', 'Ectopic pregnancy', 'First-trimester pain/bleeding; rupture risk.', mf(2, 2, 1)),
      dx('dx_abruption', 'Placental abruption', 'Painful bleeding third trimester.', mf(2, 1, 1)),
      dx('dx_previa', 'Placenta previa', 'Painless late-pregnancy bleeding.', mf(2, 1, 1)),
      dx('dx_hemorrhagic_shock', 'Hemorrhagic shock', 'Massive OB hemorrhage.', mf(2, 2, 1)),
      dx('dx_incomplete_abortion', 'Incomplete abortion', 'Retained products, infection risk.', mf(2, 2, 1)),
    ],
    requiredMimics: [mimic('m_menses', 'Heavy menstrual bleeding', ['Ectopic pregnancy'])],
    requiredDiscriminators: [
      disc('Beta-hCG', ['dx_ectopic'], 'diagnostic'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_palpitations: {
    id: 'cc_palpitations',
    chiefComplaint: 'Palpitations',
    requiredDiagnoses: [
      dx('dx_vt_vf', 'Ventricular tachycardia / VF', 'Unstable wide-complex; shock.', mf(2, 2, 1)),
      dx('dx_svt_unstable', 'SVT with hemodynamic compromise', 'Narrow complex tachycardia with hypotension.', mf(2, 2, 1)),
      dx('dx_wpw_afib', 'WPW with atrial fibrillation', 'Wide irregular tachycardia; avoid AV nodal blockers alone.', mf(2, 2, 1)),
      dx('dx_thyroid_storm', 'Thyroid storm', 'Fever, tachycardia, AMS in hyperthyroid.', mf(2, 2, 1)),
      dx('dx_pe', 'Pulmonary embolism', 'Tachycardia, dyspnea, CP.', mf(2, 1, 1)),
      dx('dx_hypokalemia', 'Hypokalemia', 'PVCs, weakness; risk in diuretics.', mf(1, 1, 1)),
    ],
    requiredMimics: [mimic('m_anxiety', 'Anxiety / panic', ['Ventricular tachycardia / VF'])],
    requiredDiscriminators: [
      disc('12-lead ECG', ['dx_vt_vf', 'dx_wpw_afib'], 'diagnostic'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_sore_throat: {
    id: 'cc_sore_throat',
    chiefComplaint: 'Sore throat',
    requiredDiagnoses: [
      dx('dx_pta', 'Peritonsillar abscess', 'Trismus, muffled voice, uvular deviation.', mf(2, 2, 0)),
      dx('dx_rpa', 'Retropharyngeal abscess', 'Pediatric; neck stiffness, toxicity.', mf(2, 2, 1)),
      dx('dx_epiglottitis', 'Epiglottitis', 'Airway emergency; drooling.', mf(2, 2, 0)),
      dx('dx_ludwig', "Ludwig's angina", 'Bilateral submandibular brawny swelling.', mf(2, 2, 1)),
      dx('dx_angioedema', 'Angioedema', 'ACE-i / hereditary; airway compromise.', mf(2, 2, 0)),
    ],
    requiredMimics: [mimic('m_pharyngitis', 'Viral pharyngitis', ['Peritonsillar abscess'])],
    requiredDiscriminators: [
      disc('Voice change / trismus', ['dx_pta'], 'history'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
  cc_leg_pain_swelling: {
    id: 'cc_leg_pain_swelling',
    chiefComplaint: 'Leg pain / swelling',
    requiredDiagnoses: [
      dx('dx_dvt', 'Deep vein thrombosis', 'PE risk; unilateral swelling/calf pain.', mf(2, 2, 1)),
      dx('dx_compartment', 'Compartment syndrome', 'Pain with passive stretch; tenseness.', mf(2, 2, 1)),
      dx('dx_nec_fasciitis', 'Necrotizing fasciitis', 'Rapid spread, systemic toxicity.', mf(2, 2, 1)),
      dx('dx_septic_arthritis', 'Septic arthritis', 'Hot swollen joint; urgent aspiration.', mf(2, 2, 1)),
      dx('dx_arterial_occlusion', 'Arterial occlusion / ALI', 'Pulseless cold limb; time-critical.', mf(2, 2, 1)),
    ],
    requiredMimics: [mimic('m_cellulitis', 'Cellulitis', ['Necrotizing fasciitis'])],
    requiredDiscriminators: [
      disc('Unilateral calf swelling / Homan sign not relied upon', ['dx_dvt'], 'exam'),
    ],
    metadata: { version: 1, createdBy: 'clinician_manual', sources: ["Tintinalli's 9e", "Rosen's 10e"] },
  },
};

/**
 * @param {string} chiefComplaintId
 * @returns {Object | null} CantMissRegistryEntry-shaped object
 */
export function getCantMissRegistry(chiefComplaintId) {
  if (!chiefComplaintId || typeof chiefComplaintId !== 'string') return null;
  return REGISTRY[chiefComplaintId] || null;
}

/**
 * @returns {string[]}
 */
export function getAllCantMissRegistryIds() {
  return Object.keys(REGISTRY);
}

export const CANT_MISS_REGISTRY_VERSION = 1;
